import { DisplayMeasurementProfileDetail, StatusData } from "@fairfleet/geotab";
import { GridApiPro } from "@mui/x-data-grid-pro";
import { closeSnackbar, OptionsObject, SnackbarKey, useSnackbar } from "notistack";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SavedFilterRef } from "@/hooks/useFocusGridRow";
import { AlertRule, AlertSeverity } from "@/models/AlertRule";
import { AlertDisplayMode } from "@/models/DataGridSettingsAddInData";
import { DeviceStatusOverviewData } from "@/models/DeviceStatusOverviewData";
import playAlertSound from "@/utilities/alertSound";
import evaluateAlertCondition from "@/utilities/evaluateAlertCondition";

type ActiveViolations = Map<string, Set<string>>; // ruleId -> Set<deviceId>
type PreviousValues = Map<string, Map<string, number | null>>; // ruleId -> Map<deviceId, value>;

/** Maps deviceId to the highest severity of any violated rule */
export type ViolatedDevices = Map<string, AlertSeverity>;
/** Maps ruleId to the set of violated deviceIds */
export type ViolationsByRule = Map<string, Set<string>>;

export interface AlertEvaluationResult {
  violatedDevices: ViolatedDevices;
  violationsByRule: ViolationsByRule;
}

const SEVERITY_RANK: Record<AlertSeverity, number> = {
  info: 0,
  warning: 1,
  critical: 2,
};

function extractValue(
  row: DeviceStatusOverviewData,
  rule: AlertRule,
  diagnosticUnits: DisplayMeasurementProfileDetail[],
): number | null {
  switch (rule.sourceType) {
    case "diagnostic": {
      const statusDataArray = (row.statusData?.statusData ?? []) as unknown as StatusData[];
      const entry = statusDataArray.find(sd => sd.diagnostic.id === rule.diagnosticId);
      if (entry?.data == null) return null;
      // Convert raw value to display unit (e.g., W -> kWh) to match what the user sees in the grid
      const unit = diagnosticUnits.find(du => du.diagnosticId === rule.diagnosticId);
      const factor = unit?.displayUnitOfMeasure?.factorFromBase;
      return factor ? entry.data * factor : entry.data;
    }
    case "field": {
      const fieldValue = (row as unknown as Record<string, unknown>)[rule.fieldId ?? ""];
      if (typeof fieldValue === "boolean") return fieldValue ? 1 : 0;
      if (typeof fieldValue !== "number") return null;
      // Normalize isCharging: AC (1) and DC (2) both count as "Ja" (1)
      if (rule.fieldId === "isCharging" && fieldValue === 2) return 1;
      return fieldValue;
    }
    case "warning":
      return row.warnings.some(w => w.type === rule.warningType) ? 1 : 0;
    default:
      return null;
  }
}

function severityToVariant(severity: AlertSeverity): "info" | "warning" | "error" {
  switch (severity) {
    case "info": return "info";
    case "warning": return "warning";
    case "critical": return "error";
  }
}

function formatValue(value: number | null, precision: number): string {
  if (value === null) return "N/A";
  return value.toFixed(precision);
}

function getThresholdText(rule: AlertRule, precision: number): string {
  switch (rule.conditionType) {
    case "above": return `> ${formatValue(rule.threshold ?? null, precision)}`;
    case "below": return `< ${formatValue(rule.threshold ?? null, precision)}`;
    case "between": return `${formatValue(rule.thresholdMin ?? null, precision)}\u2013${formatValue(rule.thresholdMax ?? null, precision)}`;
    case "outside": return `< ${formatValue(rule.thresholdMin ?? null, precision)} / > ${formatValue(rule.thresholdMax ?? null, precision)}`;
    case "equals": return `= ${formatValue(rule.threshold ?? null, precision)}`;
    case "changes": return "";
  }
}

export default function useAlertEvaluation(
  data: DeviceStatusOverviewData[],
  alertRules: AlertRule[],
  apiRef: RefObject<GridApiPro>,
  diagnosticUnits: DisplayMeasurementProfileDetail[],
  precisions: Record<string, number>,
  displayMode: AlertDisplayMode,
  autoDismiss: boolean,
  savedFilterRef?: SavedFilterRef,
): AlertEvaluationResult {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const activeViolationsRef = useRef<ActiveViolations>(new Map());
  const previousValuesRef = useRef<PreviousValues>(new Map());
  const initialLoadRef = useRef(true);
  const repeatingIntervalsRef = useRef<Map<SnackbarKey, ReturnType<typeof setInterval>>>(new Map());

  useEffect(() => {
    const intervals = repeatingIntervalsRef.current;
    return () => {
      for (const id of intervals.values()) clearInterval(id);
      intervals.clear();
    };
  }, []);
  const [violatedDevices, setViolatedDevices] = useState<ViolatedDevices>(new Map());
  const [violationsByRule, setViolationsByRule] = useState<ViolationsByRule>(new Map());

  const focusDevice = useCallback(
    (deviceName: string, deviceId: string, key: SnackbarKey) => {
      if (savedFilterRef && !savedFilterRef.current) {
        savedFilterRef.current = apiRef.current?.state?.filter?.filterModel ?? null;
      }
      apiRef.current?.setFilterModel({ items: [], quickFilterValues: [deviceName] });
      apiRef.current?.setExpandedDetailPanels([deviceId]);
      closeSnackbar(key);
    },
    [apiRef, savedFilterRef],
  );

  useEffect(() => {
    if (data.length === 0) return;

    // Skip first data load to avoid alert spam on page open
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      // Still record initial values for "changes" condition
      for (const rule of alertRules.filter(r => r.enabled)) {
        const valuesMap = new Map<string, number | null>();
        for (const row of data) {
          if (!rule.targetAll && !rule.targetDeviceIds?.includes(row.device.id)) continue;
          valuesMap.set(row.device.id, extractValue(row, rule, diagnosticUnits));
        }
        previousValuesRef.current.set(rule.id, valuesMap);
      }
      return;
    }

    const enabledRules = alertRules.filter(r => r.enabled);
    const newViolations = new Map<string, Set<string>>();
    const newViolatedDevices = new Map<string, AlertSeverity>();

    // Seed previous values for any newly added rules to avoid false alerts
    for (const rule of enabledRules) {
      if (!previousValuesRef.current.has(rule.id)) {
        const valuesMap = new Map<string, number | null>();
        for (const row of data) {
          if (!rule.targetAll && !rule.targetDeviceIds?.includes(row.device.id)) continue;
          valuesMap.set(row.device.id, extractValue(row, rule, diagnosticUnits));
        }
        previousValuesRef.current.set(rule.id, valuesMap);
      }
    }

    for (const rule of enabledRules) {
      const violated = new Set<string>();
      const prevRuleValues = previousValuesRef.current.get(rule.id);
      const newRuleValues = new Map<string, number | null>();

      for (const row of data) {
        if (!rule.targetAll && !rule.targetDeviceIds?.includes(row.device.id)) continue;

        const value = extractValue(row, rule, diagnosticUnits);
        const previousValue = prevRuleValues?.get(row.device.id) ?? null;
        newRuleValues.set(row.device.id, value);

        const isViolated = evaluateAlertCondition(rule, value, previousValue);

        if (isViolated) {
          violated.add(row.device.id);

          // Track highest severity per device
          const current = newViolatedDevices.get(row.device.id);
          if (!current || SEVERITY_RANK[rule.severity] > SEVERITY_RANK[current]) {
            newViolatedDevices.set(row.device.id, rule.severity);
          }

          const wasAlreadyViolated = activeViolationsRef.current.get(rule.id)?.has(row.device.id);
          if (!wasAlreadyViolated) {
            // New violation — fire alert
            const diagnosticKey = rule.sourceType === "diagnostic" ? (rule.diagnosticId ?? "") : "";
            const precision = diagnosticKey in precisions ? precisions[diagnosticKey] : 0;
            const message = t("alertRules.alertTriggered", {
              device: row.device.name,
              diagnostic: rule.displayName,
              value: formatValue(value, precision),
              limit: getThresholdText(rule, precision),
            });
            const deviceName = row.device.name;
            const deviceId = row.id;

            const shouldRepeat = !!(rule.soundEnabled && rule.soundRepeating);
            const intervalsMap = repeatingIntervalsRef.current;

            const options: OptionsObject = {
              variant: severityToVariant(rule.severity),
              autoHideDuration: shouldRepeat ? null : autoDismiss ? 10000 : null,
              persist: shouldRepeat,
              SnackbarProps: {
                style: { cursor: "pointer" },
                onClick: () => focusDevice(deviceName, deviceId, key),
              },
              onClose: (_event, _reason, snackbarKey) => {
                if (snackbarKey == null) return;
                const id = intervalsMap.get(snackbarKey);
                if (id !== undefined) {
                  clearInterval(id);
                  intervalsMap.delete(snackbarKey);
                }
              },
            };
            if (displayMode === "prominent") {
              options.anchorOrigin = { vertical: "top", horizontal: "center" };
              options.style = {
                fontSize: "1.15rem",
                fontWeight: 600,
                minWidth: 480,
                padding: "16px 24px",
              };
            }
            const key = enqueueSnackbar(message, options);

            if (rule.soundEnabled) {
              playAlertSound(rule.severity);
              if (shouldRepeat) {
                const intervalId = setInterval(() => playAlertSound(rule.severity), 10_000);
                intervalsMap.set(key, intervalId);
              }
            }
          }
        }
      }

      previousValuesRef.current.set(rule.id, newRuleValues);
      newViolations.set(rule.id, violated);
    }

    activeViolationsRef.current = newViolations;
    setViolatedDevices(newViolatedDevices);
    setViolationsByRule(newViolations);
  }, [data, alertRules, enqueueSnackbar, t, apiRef, focusDevice, diagnosticUnits, precisions, displayMode, autoDismiss]);

  return { violatedDevices, violationsByRule };
}
